import { createFileRoute, Link, useRouterState } from "../lib/router-compat";
import { ClassesContent, ClarionContent, MainePanContent } from "../components/pan-content";

import React, { Suspense, useEffect, useRef, useState } from "react";
import { Menu, X } from "lucide-react";
import bassImg from "../assets/pan-bass-w.jpg";
import tenorImg from "../assets/pan-tenor-c.png";
import celloImg from "../assets/pan-cello-w.jpg";
import { onOverlay, takePendingOverlay } from "../lib/overlay-bus";
import { ROUTE_CONTENT, PAN_PATH_TO_KEY } from "../lib/route-registry";
import { SiteFooter } from "../components/SiteFooter";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "The Pan Institute — v12" },
      { name: "description", content: "Cinematic three-pan landing for the Pan Institute." },
    ],
  }),
  component: V1,
});


type PanKey = "education" | "productions" | "clarion";
type SecondaryKey = "calendar" | "history" | "contact";
type OverlayKey = PanKey | SecondaryKey;

const PANS: Array<{
  key: PanKey;
  label: string;
  copy: string;
  body: string;
  img: string;
  pos: "left" | "middle" | "right";
  tint: string;
}> = [
  {
    key: "education",
    label: "EDUCATION",
    copy: "Workshops, lessons, and youth programs that put a pan in every hand.",
    body: "From first notes to advanced arranging, the Institute trains players, teachers, and tuners. Group classes, private lessons, and in-school residencies build a steady pipeline of musicians grounded in the steelpan tradition.",
    img: celloImg,
    pos: "left",
    tint: "radial-gradient(circle at center, rgba(210,150,45,.2), rgba(0,0,0,.84)), rgba(0,0,0,.68)",
  },
  {
    key: "productions",
    label: "MAINE PAN PRODUCTIONS",
    copy: "Performances, events, music creation and live cultural experiences.",
    body: "Our recording and publishing arm — original works, commissioned arrangements, and releases that carry the pan into new rooms. We produce concerts, festivals, and studio sessions for our ensembles and partners.",
    img: bassImg,
    pos: "middle",
    tint: "radial-gradient(circle at center, rgba(220,220,220,.18), rgba(0,0,0,.84)), rgba(0,0,0,.68)",
  },
  {
    key: "clarion",
    label: "ATLANTIC CLARION",
    copy: "A polished steel sound — our flagship ensemble and concert programmes.",
    body: "Concert programs, festival appearances, and a touring repertoire built around the full pan family. The Atlantic Clarion is the Institute's flagship ensemble, presenting the steelpan as a serious concert voice.",
    img: tenorImg,
    pos: "right",
    tint: "radial-gradient(circle at center, rgba(255,255,255,.1), rgba(0,0,0,.84)), rgba(0,0,0,.68)",
  },
];

const SECONDARY: Record<SecondaryKey, { label: string; tag: string; body: string }> = {
  calendar: {
    label: "Calendar",
    tag: "What's on",
    body: "Concerts, classes, and open rehearsals. Check back for the upcoming season — full schedule arriving soon.",
  },
  history: {
    label: "History",
    tag: "How we got here",
    body: "Founded around three voices of the steelpan — bass, tenor, and cello — the Institute exists to teach, create, and perform. A longer history is being written.",
  },
  contact: {
    label: "Contact",
    tag: "Say hello",
    body: "For lessons, bookings, press, or partnership enquiries, reach out at hello@paninstitute.org and we'll be in touch.",
  },
};

const SECONDARY_PATH_TO_KEY: Record<string, SecondaryKey> = {};

function isPan(key: OverlayKey | null): key is PanKey {
  return key === "education" || key === "productions" || key === "clarion";
}

const PAN_CONTENT: Record<PanKey, React.ComponentType> = {
  education: ClassesContent,
  productions: MainePanContent,
  clarion: ClarionContent,
};

function V1() {
  const stageRef = useRef<HTMLDivElement>(null);
  const focusRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLDivElement>(null);
  const circleRefs = useRef<Record<PanKey, HTMLButtonElement | null>>({
    education: null,
    productions: null,
    clarion: null,
  });

  const [loaded, setLoaded] = useState(false);
  const labelStyle = "upright";
  const [overlay, setOverlay] = useState<OverlayKey | null>(null);
  const [travelling, setTravelling] = useState(false);
  const [routeOverlay, setRouteOverlay] = useState<string | null>(null);
  const isAnimating = useRef(false);
  const parallax = useRef({ x: 0, y: 0 });
  const zoomingKey = useRef<PanKey | null>(null);

  const activePan = isPan(overlay) ? PANS.find((p) => p.key === overlay)! : null;
  const activeSecondary = overlay && !isPan(overlay) ? SECONDARY[overlay] : null;
  const RouteOverlayComp = routeOverlay ? ROUTE_CONTENT[routeOverlay] : null;
  const overlayOpen = !!(activePan || activeSecondary || RouteOverlayComp);

  // Lock body scroll when any overlay is open so the homepage underneath can't be scrolled
  useEffect(() => {
    if (overlayOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = "0";
      document.body.style.right = "0";
      document.body.style.overflow = "hidden";
      document.body.classList.add("pi-overlay-open");
      return () => {
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.left = "";
        document.body.style.right = "";
        document.body.style.overflow = "";
        document.body.classList.remove("pi-overlay-open");
        window.scrollTo(0, scrollY);
      };
    }
  }, [overlayOpen]);

  useEffect(() => {
    const id = requestAnimationFrame(() => setLoaded(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const routeOverlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handle = (path: string) => {
      if (ROUTE_CONTENT[path]) {
        setOverlay(null);
        setRouteOverlay(path);
      } else if (SECONDARY_PATH_TO_KEY[path]) {
        setRouteOverlay(null);
        openSecondary(SECONDARY_PATH_TO_KEY[path]);
      } else {
        const panKey = PAN_PATH_TO_KEY[path];
        if (panKey) {
          setRouteOverlay(null);
          openPan(panKey);
        }
      }
      // Scroll overlay to top when swapping content
      if (routeOverlayRef.current) {
        routeOverlayRef.current.scrollTo(0, 0);
      }
    };
    const pending = takePendingOverlay();
    if (pending) handle(pending.path);
    return onOverlay(({ path }) => handle(path));
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeAll();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  });

  function openPan(key: PanKey) {
    if (isAnimating.current) return;
    setOverlay(key);
  }

  function openSecondary(key: SecondaryKey) {
    if (isAnimating.current) return;
    setOverlay(key);
  }

  function closeAll() {
    if (!overlay && !routeOverlay) return;
    setOverlay(null);
    setRouteOverlay(null);
    setTravelling(false);
    zoomingKey.current = null;
  }

  const pageClasses = [
    loaded && "loaded",
    travelling && "travelling",
    isPan(overlay) && "active",
    activeSecondary && "secondary-active",
  ]
    .filter(Boolean)
    .join(" ");

  const tint = activePan?.tint;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <style>{css}</style>

      <section
        ref={heroRef}
        className={`pi-page relative isolate min-h-screen flex flex-col labels-${labelStyle} ${pageClasses}`}
      >
        <div className="pi-blur-wrap flex-1 flex flex-col w-full">
          <div
            aria-hidden
            className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top,oklch(0.18_0.01_250)_0%,oklch(0.1_0.005_250)_60%,oklch(0.08_0.005_250)_100%)]"
          />
          <div
            aria-hidden
            className="absolute inset-x-0 bottom-0 -z-10 h-1/3 bg-gradient-to-t from-black/60 to-transparent"
          />

          <div className="flex-1 flex flex-col items-center px-6 pt-8 pb-2">
            <div className="relative w-full flex-1 min-h-[560px]">
              <div className="pi-stage" ref={stageRef}>
                {PANS.map((p) => (
                  <div key={p.key} className={`pi-slot pi-slot-${p.pos}`}>
                    <button
                      ref={(el) => {
                        circleRefs.current[p.key] = el;
                      }}
                      className="pi-circle"
                      type="button"
                      onClick={() => {
                        const path = Object.entries(PAN_PATH_TO_KEY).find(([, k]) => k === p.key)?.[0];
                        if (path) {
                          setOverlay(null);
                          setRouteOverlay(path);
                        }
                      }}
                      aria-label={p.label}
                    >
                      <span className="pi-halo" aria-hidden />
                      <span className="pi-ring" aria-hidden />
                      <img src={p.img} alt={`${p.label} pan`} className={`pi-pan-img pi-pan-img-${p.key}`} />
                      <span className="pi-glare" aria-hidden />
                    </button>
                    <span className="pi-pan-label">
                      <span className="pi-pan-label-text">{p.label}</span>
                      <span className="pi-pan-label-inner" aria-hidden>
                        {p.label.split("").map((char, index) => (
                          <span key={`${p.key}-${index}`} className={char === " " ? "pi-label-space" : undefined}>
                            {char === " " ? "\u00A0" : char}
                          </span>
                        ))}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>



      {/* UNIFIED OVERLAY — pan content, secondary content, and any other route */}
      {(activePan || activeSecondary || RouteOverlayComp) && (
        <div className="pi-route-overlay" onClick={closeAll} ref={routeOverlayRef}>
          <button
            type="button"
            className="pi-close pi-close-route"
            onClick={(e) => {
              e.stopPropagation();
              closeAll();
            }}
            aria-label="Close"
          >
            ×
          </button>
          <div className="pi-route-scroll" onClick={(e) => e.stopPropagation()}>
            {activePan && (() => {
              const C = PAN_CONTENT[activePan.key];
              return (
                <div className="mx-auto max-w-5xl px-6 pt-28 pb-24 text-foreground">
                  <div className="text-[10px] uppercase tracking-[0.5em] text-primary mb-4 text-center">
                    {activePan.label}
                  </div>
                  <h2 className="font-display italic text-4xl md:text-7xl text-center tracking-tight mb-12">
                    {activePan.label}
                  </h2>
                  <div className="pi-rich"><C /></div>
                </div>
              );
            })()}
            {activeSecondary && (
              <div className="mx-auto max-w-2xl px-6 pt-40 pb-24 text-center text-foreground">
                <div className="text-[10px] uppercase tracking-[0.5em] text-primary mb-4">
                  {activeSecondary.tag}
                </div>
                <h2 className="font-display italic text-4xl md:text-7xl tracking-tight mb-6">
                  {activeSecondary.label}
                </h2>
                <p className="text-lg text-foreground/75 leading-relaxed">{activeSecondary.body}</p>
              </div>
            )}
            {RouteOverlayComp && (
              <Suspense fallback={<div className="pt-40 text-center text-foreground/60">Loading…</div>}>
                <RouteOverlayComp />
                <SiteFooter />
              </Suspense>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function V1Nav({
  onOpen,
  onOpenPan,
}: {
  onOpen: (k: SecondaryKey) => void;
  onOpenPan: (k: PanKey) => void;
}) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const links: Array<{ key: OverlayKey; label: string; pan?: boolean }> = [
    { key: "education", label: "Education", pan: true },
    { key: "productions", label: "Maine Pan Productions", pan: true },
    { key: "clarion", label: "Atlantic Clarion", pan: true },
    { key: "calendar", label: "Calendar" },
    { key: "history", label: "History" },
    { key: "contact", label: "Contact" },
  ];

  const handle = (l: (typeof links)[number]) => {
    if (l.pan) onOpenPan(l.key as PanKey);
    else onOpen(l.key as SecondaryKey);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-[120] transition-all duration-500 ${
        scrolled || open
          ? "bg-background/80 backdrop-blur-xl border-b border-border"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6">
        <Link
          to="/v12"
          className="font-display text-lg tracking-[0.25em] uppercase text-foreground hover:text-primary transition-colors"
        >
          Pan Institute
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.key}
              type="button"
              onClick={() => handle(l)}
              className="text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-primary transition-colors"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <button
          aria-label="Toggle menu"
          className="md:hidden text-foreground"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <nav className="md:hidden border-t border-border bg-background/95 backdrop-blur-xl">
          <ul className="flex flex-col px-6 py-4 gap-4">
            {links.map((l) => (
              <li key={l.key}>
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    handle(l);
                  }}
                  className="block text-sm uppercase tracking-[0.2em] text-muted-foreground hover:text-primary"
                >
                  {l.label}
                </button>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </header>
  );
}

const css = `
.pi-stage {
  position: absolute; inset: 0; z-index: 5;
  transform: translate3d(0,0,0);
  transition: transform 700ms cubic-bezier(.2,.8,.2,1), filter 400ms ease, opacity 400ms ease;
  will-change: transform;
}

.pi-slot {
  position: absolute;
  display: flex; align-items: center; gap: 22px;
  transition: opacity 700ms ease, filter 700ms ease, transform 700ms ease;
}
.pi-slot-left   { left: 38%; top: 36%; transform: translate(-50%,-50%); z-index: 3; flex-direction: row-reverse; }
.pi-slot-right  { left: 60%; top: 42%; transform: translate(-50%,-50%); z-index: 4; flex-direction: row; }
.pi-slot-middle { left: 50%; top: 68%; transform: translate(-50%,-50%); z-index: 5; flex-direction: column; gap: 18px; }

.pi-slot-left .pi-pan-label,
.pi-slot-right .pi-pan-label {
  writing-mode: vertical-rl;
  font-size: 12px;
}
.labels-upright .pi-slot-left .pi-pan-label,
.labels-upright .pi-slot-right .pi-pan-label {
  text-orientation: upright;
  letter-spacing: .15em;
}
.labels-rotated .pi-slot-left .pi-pan-label,
.labels-rotated .pi-slot-right .pi-pan-label {
  text-orientation: mixed;
  letter-spacing: .45em;
}
.labels-rotated .pi-slot-left .pi-pan-label { transform: rotate(180deg); }

.pi-circle {
  position: relative;
  width: 480px; height: 480px;
  border-radius: 50%;
  border: none; cursor: pointer;
  padding: 0; background: transparent;
  display: block;
  transition: transform 700ms cubic-bezier(.16,.84,.24,1), filter 500ms ease;
}
.pi-circle:hover { transform: translateY(-4px); filter: brightness(1.05); }

.pi-halo {
  position: absolute;
  inset: -55%;
  border-radius: 50%;
  background:
    radial-gradient(circle at 50% 45%, rgba(210,150,45,.28) 0%, rgba(210,150,45,.08) 28%, transparent 60%);
  filter: blur(14px);
  pointer-events: none;
  z-index: 0;
  opacity: .85;
  transition: opacity 500ms ease;
}
.pi-circle:hover .pi-halo { opacity: 1; }

.pi-ring {
  position: absolute; inset: -10px;
  border-radius: 50%;
  background:
    conic-gradient(from 210deg, rgba(255,255,255,.18), rgba(0,0,0,.6) 30%, rgba(255,255,255,.12) 55%, rgba(0,0,0,.7) 80%, rgba(255,255,255,.18));
  padding: 1px;
  -webkit-mask: radial-gradient(circle, transparent 60%, #000 61%);
          mask: radial-gradient(circle, transparent 60%, #000 61%);
  pointer-events: none;
  z-index: 1;
  opacity: .9;
}

.pi-pan-img {
  position: relative; z-index: 2;
  width: 100%; height: 100%;
  object-fit: cover;
  border-radius: 50%;
  box-shadow:
    inset 0 6px 22px rgba(0,0,0,.55),
    inset 0 -10px 30px rgba(0,0,0,.45),
    0 28px 60px -10px rgba(0,0,0,.7),
    0 0 0 1px rgba(255,255,255,.04);
}

.pi-glare {
  position: absolute; left: 8%; top: 6%;
  width: 60%; height: 40%;
  border-radius: 50%;
  background: radial-gradient(ellipse at center, rgba(255,255,255,.22), transparent 65%);
  pointer-events: none;
  z-index: 3;
  filter: blur(2px);
}

.pi-pan-label {
  position: relative; z-index: 4;
  font-size: 11px;
  letter-spacing: .35em;
  font-weight: 500;
  color: rgba(255,255,255,.75);
  text-transform: uppercase;
  pointer-events: none;
}
.pi-pan-label-inner { display: none; }
.pi-pan-label-text { display: inline; }

.pi-page:not(.loaded) .pi-slot { opacity: 0; filter: blur(12px); }
.pi-page.loaded .pi-slot { opacity: 1; filter: blur(0); }
.pi-page.loaded .pi-slot-middle { transition-delay: 120ms; }
.pi-page.loaded .pi-slot-left   { transition-delay: 260ms; }
.pi-page.loaded .pi-slot-right  { transition-delay: 360ms; }

@media (max-width: 1200px) {
  .pi-circle { width: 360px; height: 360px; }
}
@media (max-width: 700px) {
  .pi-circle { width: 240px; height: 240px; }
  .pi-pan-img-productions {
    object-fit: contain;
    background: #000;
  }
  .pi-slot { gap: 0; align-items: center; justify-content: center; }
  .pi-slot-left   { left: 50%; top: 22%; transform: translate(-50%,-50%); }
  .pi-slot-right  { left: 50%; top: 45%; transform: translate(-50%,-50%); }
  .pi-slot-middle { left: 50%; top: 70%; transform: translate(-50%,-50%); }
  .pi-pan-label,
  .pi-slot-left .pi-pan-label,
  .pi-slot-right .pi-pan-label,
  .pi-slot-middle .pi-pan-label {
    position: absolute;
    top: 0;
    bottom: 0;
    height: auto;
    display: flex;
    align-items: center;
    justify-content: center;
    transform: none;
    writing-mode: horizontal-tb;
    pointer-events: none;
  }
  .pi-pan-label-inner { display: none; }
  .pi-pan-label-text {
    display: inline-block;
    transform: rotate(-90deg);
    transform-origin: center;
    white-space: nowrap;
    font-size: 12px;
    letter-spacing: .18em;
    line-height: 1;
  }
  .pi-slot-left .pi-pan-label   { right: calc(50% + 130px); left: auto; width: 24px; }
  .pi-slot-right .pi-pan-label  { left: calc(50% + 130px); right: auto; width: 24px; }
  .pi-slot-middle .pi-pan-label { right: calc(50% + 130px); left: auto; width: 24px; }
}

.pi-focus-circle {
  position: fixed; left: 50%; top: 50%;
  width: 230px; height: 230px;
  border-radius: 50%;
  opacity: 0;
  transform: translate(-50%,-50%) scale(1);
  z-index: 1; pointer-events: none;
  overflow: hidden;
  transition: transform 900ms cubic-bezier(.16,.84,.24,1), opacity 450ms ease, filter 900ms ease;
}

.pi-overlay {
  position: fixed; inset: 0;
  background: #000;
  opacity: 0; z-index: 15; pointer-events: none;
  transition: opacity 400ms ease;
}

.pi-content {
  position: fixed; inset: 0;
  z-index: 30;
  opacity: 0; pointer-events: none;
  overflow-y: auto;
  transition: opacity 420ms ease 100ms;
  color: white;
}
.pi-content-inner {
  max-width: 1100px;
  margin: 0 auto;
  padding: 110px 24px 120px;
  text-align: center;
}
.pi-content h2 {
  font-size: clamp(42px, 8vw, 100px);
  letter-spacing: -.04em; line-height: .9;
  margin: 0;
  font-family: 'Cormorant Garamond', serif;
  font-weight: 500;
  font-style: italic;
}
.pi-content p {
  margin: 20px auto 0; max-width: 600px;
  opacity: .85; font-size: 18px; line-height: 1.5;
}
.pi-rich { color: var(--foreground); }

.pi-close {
  position: fixed; top: 80px; right: 24px;
  width: 48px; height: 48px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
  border: 1px solid rgba(255,255,255,.5);
  background: rgba(0,0,0,.55);
  color: white; font-size: 26px; line-height: 1;
  z-index: 130; opacity: 0; pointer-events: none;
  backdrop-filter: blur(12px);
  transition: opacity 300ms ease, background 250ms ease;
}
.pi-close:hover { background: rgba(255,255,255,.18); }

.pi-page.travelling .pi-headline {
  opacity: 0; transform: translateY(-10px); filter: blur(4px);
  transition: opacity 400ms ease, transform 400ms ease, filter 400ms ease;
}
.pi-page.active .pi-slot { opacity: 0; pointer-events: none; }
.pi-page.active .pi-slot:has(.pi-circle-zooming) { opacity: 1; }
.pi-circle-zooming { z-index: 50; transform-origin: center center; }
.pi-circle-zooming .pi-halo,
.pi-circle-zooming .pi-ring,
.pi-circle-zooming .pi-glare { opacity: 0; transition: opacity 300ms ease; }
.pi-page.active ~ .pi-overlay,
.pi-page.active + .pi-overlay { /* no-op, selectors handled below */ }

/* When a pan is active */
body:has(.pi-page.active) .pi-overlay { opacity: var(--pi-overlay-alpha, .20); pointer-events: auto; }
body:has(.pi-page.active) .pi-content { opacity: 1; pointer-events: auto; }
body:has(.pi-page.active) .pi-close { opacity: 1; pointer-events: auto; }
body:has(.pi-page.active) .pi-focus-circle { opacity: 0; }

/* Closing: fade text & overlay BEFORE the pan shrinks back */
body.pi-closing .pi-content {
  opacity: 0 !important;
  transition: opacity 320ms ease;
}
body.pi-closing .pi-overlay {
  opacity: .35 !important;
  backdrop-filter: blur(2px);
  transition: opacity 360ms ease 40ms, backdrop-filter 360ms ease 40ms;
}
body.pi-closing .pi-close {
  opacity: 0 !important;
  transition: opacity 220ms ease;
}

/* Secondary overlay: blur the entire page in place */
.pi-blur-wrap { transition: filter 500ms ease, transform 500ms ease; }
.pi-page.secondary-active .pi-blur-wrap { filter: blur(14px) brightness(.55); transform: scale(1.02); pointer-events: none; }

.pi-secondary-overlay {
  position: fixed; inset: 0;
  background: rgba(0,0,0,.55);
  opacity: 0; pointer-events: none;
  z-index: 110;
  display: flex; align-items: center; justify-content: center;
  padding: 24px;
  transition: opacity 400ms ease;
}
body:has(.pi-page.secondary-active) .pi-secondary-overlay { opacity: 1; pointer-events: auto; }
body:has(.pi-page.secondary-active) .pi-close { opacity: 1; pointer-events: auto; }

.pi-secondary-content {
  max-width: 720px; width: 100%;
  text-align: center; color: white;
  transform: translateY(12px);
  opacity: 0;
  transition: opacity 500ms ease 120ms, transform 500ms ease 120ms;
}
body:has(.pi-page.secondary-active) .pi-secondary-content {
  transform: translateY(0); opacity: 1;
}
.pi-secondary-tag {
  font-size: 10px; letter-spacing: .4em; text-transform: uppercase;
  color: var(--copper-glow, #d2962d); margin-bottom: 18px;
}
.pi-secondary-content h2 {
  font-family: 'Cormorant Garamond', serif;
  font-style: italic; font-weight: 500;
  font-size: clamp(42px, 7vw, 84px);
  letter-spacing: -.03em; line-height: 1; margin: 0;
}
.pi-secondary-content p {
  margin: 22px auto 0; max-width: 560px;
  opacity: .85; font-size: 17px; line-height: 1.6;
}

@media (max-width: 700px) {
  .pi-focus-circle { width: 200px; height: 200px; }
}

.pi-route-overlay {
  position: fixed; inset: 0; z-index: 140;
  background: rgba(0,0,0, var(--pi-overlay-alpha, .20));
  overflow-y: auto;
  animation: pi-fade-in 320ms ease both;
}
.pi-route-scroll { min-height: 100%; position: relative; z-index: 1; overscroll-behavior: contain; }
.pi-close-route { z-index: 150; opacity: 1 !important; pointer-events: auto !important; }

/* Nav bar always visible when overlay is open */
body.pi-overlay-open header {
  background: rgba(0,0,0,.55) !important;
  backdrop-filter: blur(16px) !important;
  border-bottom-color: rgba(255,255,255,.1) !important;
}

@keyframes pi-fade-in { from { opacity: 0; } to { opacity: 1; } }
`;
